/**
 * DailyAwarenessSection — Reclaim our Presence · Daily Awareness grid.
 *
 * Renders the 12 short Activation cards (Wake Up, Morning Light,
 * Hydration … Sleep) inside `/reclaim-presence`, sitting between the
 * hero/quick-start and the 21-day immersion cards.
 *
 * Each card opens `/reclaim-presence/activation/<slug>` where the
 * audio plays. Cards in "coming soon" state are tappable too (the
 * detail page renders the script + a "we're rendering this one"
 * placeholder) so the catalogue feels alive even before every audio
 * is baked.
 */
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight, Headphones, Clock } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORIES = ["Morning", "Day", "Evening"];

export default function DailyAwarenessSection() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/presence/activations`, { cache: "no-store" });
        if (r.ok && !cancelled) setData(await r.json());
      } catch (_) { /* silent — section just won't render */ }
    })();
    return () => { cancelled = true; };
  }, []);

  if (!data) return null;

  // Group cards by category so the morning / day / evening rhythm is
  // visually obvious in the section — matches the way the user thinks
  // about their day.
  const grouped = CATEGORIES.map((cat) => ({
    cat,
    items: data.items.filter((c) => c.category === cat),
  }));

  return (
    <section
      className="px-4 pt-2 pb-10"
      data-testid="daily-awareness-section"
    >
      {/* Section heading */}
      <div className="text-center mb-6">
        <p className="text-[10px] uppercase tracking-[0.28em] text-emerald-500/70 mb-2">
          Daily Awareness
        </p>
        <h2 className="font-['Playfair_Display'] text-2xl text-[#F2EFE9] mb-2">
          Awareness in the everyday
        </h2>
        <p className="text-[#8A8A88] text-sm leading-relaxed max-w-md mx-auto">
          Short installations that turn familiar daily moments into conscious rituals.
          {" "}
          <span className="text-emerald-400/70">{data.ready_count} of {data.total} ready</span>
        </p>
      </div>

      <div className="space-y-6">
        {grouped.map(({ cat, items }) => (
          <div key={cat}>
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#6A6A68] mb-2 px-1">
              {cat}
            </p>
            <div className="space-y-2">
              {items.map((card) => (
                <ActivationCard key={card.slug} card={card} onOpen={() => navigate(`/reclaim-presence/activation/${card.slug}`)} />
              ))}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

function ActivationCard({ card, onOpen }) {
  const ready = card.ready;
  return (
    <button
      onClick={onOpen}
      className={`w-full text-left rounded-xl border px-4 py-3 flex items-center justify-between gap-3 transition-colors ${
        ready
          ? "bg-[#13130F] border-emerald-700/30 hover:border-emerald-600/60"
          : "bg-[#0F0F0D] border-[#1F1F1B] hover:border-[#2A2A26]"
      }`}
      data-testid={`presence-activation-card-${card.slug}`}
    >
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2 mb-1">
          <h3 className={`font-medium text-sm truncate ${ready ? "text-[#F2EFE9]" : "text-[#9A9A98]"}`}>
            {card.title}
          </h3>
          {!ready && (
            <span className="text-[9px] uppercase tracking-wider text-[#6A6A68] border border-[#2A2A26] rounded-full px-1.5 py-0.5">
              coming
            </span>
          )}
          {card.is_external && (
            <span className="text-[9px] uppercase tracking-wider text-emerald-500/70 border border-emerald-700/40 rounded-full px-1.5 py-0.5">
              linked
            </span>
          )}
        </div>
        <p className="text-xs text-[#7A7A78] truncate">{card.subtitle}</p>
        {card.narrator_byline && (
          <p
            className="mt-1 text-[10px] italic tracking-wide truncate"
            style={{ color: "#C9A46A", fontFamily: '"Cormorant Garamond", serif' }}
            data-testid={`activation-narrator-${card.slug}`}
          >
            {card.narrator_byline}
          </p>
        )}
        <div className="flex items-center gap-2 mt-1 text-[10px] text-[#5A5A58]">
          {ready ? <Headphones size={10} /> : <Clock size={10} />}
          <span>{card.duration_label}</span>
        </div>
      </div>
      <ChevronRight size={16} className={ready ? "text-emerald-500/70" : "text-[#4A4A48]"} />
    </button>
  );
}
